import { launchError, launchSuccess } from "components/notifications";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { fetchSignalProviderInfos, updateSignalProviderInfos } from "services/copyInvestServices";
import './userSignalProvider.css'
import { uploadUserPicture } from "services/userServices";

const DEFAULT_FORM = {
  is_signal_provider: 'NONE',
  bio: '',
  revenue_sharing: 0,
  followers_threshold: 0,
  investment_threshold: 0,
  // next_revenue_sharing: 0,
  // max_followers: 0,
  experience: 0,
  horizon: 'long_term',
  avg_performance: 0,
  objective_gain_amount: 0,
  video_url: '',
  // trades_threshold: 0,
  is_private: true,
  unlock_code: '',
  fake_annualized_performance: 0,
  is_in_simulator: false,
}

const isFormComplete = (form) => {
  if (form.is_signal_provider === 'NONE') return true;
  return (
    form.bio.trim() !== '' &&
    // form.video_url.trim() !== '' &&
    form.horizon !== '' &&
    form.experience > 0 &&
    form.revenue_sharing > 0 &&
    // form.next_revenue_sharing > 0 &&
    form.followers_threshold > 0 &&
    // form.investment_threshold > 0 &&
    // form.max_followers > 0 &&
    form.trades_threshold > 0 &&
    form.avg_performance > 0 &&
    form.objective_gain_amount > 0
  );
}

const UserSignalProvider = ({ userData, setUserData, user, id }) => {
  const { t } = useTranslation();

  const [spData, setSpData] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [coverPictureFile, setCoverPictureFile] = useState(null);
  const [coverPicturePreview, setCoverPicturePreview] = useState(null);
  const coverPictureInputRef = useRef(null);

  useEffect(() => {
    if (user && id && ['CLASSIC', 'MARGIN', 'BOTH'].includes(userData.is_signal_provider)) {
      getSpData();
    }
  }, [user, id])

  const getSpData = async () => {
    try {
      const data = await fetchSignalProviderInfos(user, id);
      setSpData(data);
      setForm({ ...DEFAULT_FORM, ...data, is_signal_provider: userData.is_signal_provider });
      setCoverPicturePreview(null);
      setCoverPictureFile(null);
    } catch {
      // user has no SP data yet — keep defaults
    }
  }

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleCoverPictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverPictureFile(file);
    setCoverPicturePreview(URL.createObjectURL(file));
  }

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  })

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await updateSignalProviderInfos(user, id, form);

      if (form['unlock_code'])
        setForm(prev => ({ ...prev, unlock_code: prev['unlock_code']?.trim()?.toUpperCase() }))
      setUserData(prev => ({ ...prev, is_signal_provider: form.is_signal_provider }))

      if (coverPictureFile) {
        const base64 = await toBase64(coverPictureFile);
        const data = await uploadUserPicture({ user, id, type: 'cover', picture: base64 })
        setCoverPictureFile(null);
        setSpData(prev => ({ ...prev, cover_picture: data.cover_picture }))
      }
      launchSuccess(t('notifications.users.userById.signal_provider.success'))
    } catch (error) {
      console.error(error)
      launchError(t('notifications.users.userById.signal_provider.error'))
    } finally {
      setLoading(false);
    }
  }

  const isNone = form.is_signal_provider === 'NONE';
  const canSave = isFormComplete(form);

  return (
    <div className='sp_container'>
      <p className='sp_main_title'>Signal Provider</p>

      {/* Type — always visible */}
      <div className='sp_section'>
        <p className='sp_section_title'>Type de signal provider</p>
        <div className='sp_type_row'>
          <div className='sp_field'>
            {/* <label className='sp_label' htmlFor='sp_type'>Type</label> */}
            <select
              id='sp_type'
              className='custom-select'
              value={form.is_signal_provider}
              onChange={(e) => handleChange('is_signal_provider', e.target.value)}
            >
              <option value='NONE'>Aucun</option>
              <option value='CLASSIC'>Classique</option>
              <option value='MARGIN'>Margin</option>
              <option value='BOTH'>Classique & Margin</option>
            </select>
          </div>
        </div>
      </div>

      {!isNone && <>
        {/* Infos de base */}
        <div className='sp_section'>
          <p className='sp_section_title'>Informations de base</p>
          <div className='sp_fields_grid'>
            <div className='sp_field_full'>
              <label className='sp_label' htmlFor='sp_bio'>Bio</label>
              <textarea
                id='sp_bio'
                className='sp_textarea'
                value={form.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                rows={3}
              />
            </div>
            <div className='sp_field_full'>
              <label className='sp_label'>Photo de couverture</label>
              <div className='sp_cover_picture_row'>
                {(coverPicturePreview || spData?.cover_picture) && (
                  <img
                    src={coverPicturePreview ?? spData.cover_picture}
                    alt='cover preview'
                    className='sp_cover_picture_preview'
                  />
                )}
                <button
                  type='button'
                  className='sp_cover_picture_button'
                  onClick={() => coverPictureInputRef.current?.click()}
                >
                  {spData?.cover_picture || coverPicturePreview ? 'Changer l\'image' : 'Ajouter une image'}
                </button>
                {coverPictureFile && (
                  <span className='sp_cover_picture_filename'>{coverPictureFile.name}</span>
                )}
                <input
                  ref={coverPictureInputRef}
                  type='file'
                  accept='image/*'
                  style={{ display: 'none' }}
                  onChange={handleCoverPictureChange}
                />
              </div>
            </div>
            <div className='sp_field_full'>
              <label className='sp_label' htmlFor='sp_video_url'>URL Vidéo</label>
              <input
                id='sp_video_url'
                type='url'
                className='sp_input sp_input_wide'
                value={form.video_url}
                onChange={(e) => handleChange('video_url', e.target.value)}
                placeholder='https://...'
              />
            </div>
            <div className='sp_field'>
              <label className='sp_label' htmlFor='sp_horizon'>Horizon</label>
              <select
                id='sp_horizon'
                className='custom-select'
                value={form.horizon}
                onChange={(e) => handleChange('horizon', e.target.value)}
              >
                <option value='short_term'>Court terme</option>
                <option value='long_term'>Long terme</option>
              </select>
            </div>
            <div className='sp_field'>
              <label className='sp_label' htmlFor='sp_experience'>Expérience (années)</label>
              <input
                id='sp_experience'
                type='number'
                className='sp_input'
                value={form.experience}
                onChange={(e) => handleChange('experience', Number(e.target.value))}
              />
            </div>
          </div>
        </div>

        {/* Seuils & partage */}
        <div className='sp_section'>
          <p className='sp_section_title'>Seuils & partage des revenus</p>
          <div className='sp_fields_grid'>
            <div className='sp_field'>
              <label className='sp_label' htmlFor='sp_revenue_sharing'>Revenue sharing (%)</label>
              <input
                id='sp_revenue_sharing'
                type='number'
                className='sp_input'
                value={form.revenue_sharing}
                onChange={(e) => handleChange('revenue_sharing', Number(e.target.value))}
              />
            </div>
            <div className='sp_field'>
              <label className='sp_label' htmlFor='sp_followers_threshold'>Seuil followers</label>
              <input
                id='sp_followers_threshold'
                type='number'
                className='sp_input'
                value={form.followers_threshold}
                onChange={(e) => handleChange('followers_threshold', Number(e.target.value))}
              />
            </div>
            <div className='sp_field'>
              <label className='sp_label' htmlFor='sp_trades_threshold'>Seuil de trades</label>
              <input
                id='sp_trades_threshold'
                type='number'
                className='sp_input'
                value={form.trades_threshold}
                onChange={(e) => handleChange('trades_threshold', Number(e.target.value))}
              />
            </div>
            <div className='sp_field'>
              <label className='sp_label' htmlFor='sp_objective_gain'>Gains sur objectifs (€)</label>
              <input
                id='sp_objective_gain'
                type='number'
                className='sp_input'
                value={form.objective_gain_amount}
                onChange={(e) => handleChange('objective_gain_amount', Number(e.target.value))}
              />
            </div>
          </div>
        </div>

        {/* Performance */}
        <div className='sp_section'>
          <p className='sp_section_title'>Performance</p>
          <div className='sp_fields_grid'>
            <div className='sp_field'>
              <label className='sp_label' htmlFor='sp_avg_performance'>Rentabilité cible (%)</label>
              <input
                id='sp_avg_performance'
                type='number'
                className='sp_input'
                value={form.avg_performance}
                onChange={(e) => handleChange('avg_performance', Number(e.target.value))}
              />
            </div>
            <div className='sp_field'>
              <label className='sp_label' htmlFor='sp_fake_annualized'>Performance annualisée simulator (%)</label>
              <input
                id='sp_fake_annualized'
                type='number'
                className='sp_input'
                value={form.fake_annualized_performance}
                onChange={(e) => handleChange('fake_annualized_performance', Number(e.target.value))}
              />
            </div>

          </div>
        </div>

        {/* Paramètres */}
        <div className='sp_section'>
          <p className='sp_section_title'>Paramètres</p>
          <div className='sp_fields_grid'>
            <div className='sp_field'>
              <label className='sp_label' htmlFor='sp_unlock_code'>Code de déverrouillage</label>
              <input
                id='sp_unlock_code'
                type='text'
                className='sp_input'
                value={form.unlock_code}
                onChange={(e) => handleChange('unlock_code', e.target.value?.toUpperCase())}
                placeholder='ABCD'
              />
            </div>
            <div className='sp_field sp_field_toggle'>
              <label className='sp_label'>Privé</label>
              <button
                className={`sp_toggle ${form.is_private ? 'sp_toggle_on' : 'sp_toggle_off'}`}
                onClick={() => handleChange('is_private', !form.is_private)}
                type='button'
              >
                {form.is_private ? 'Oui' : 'Non'}
              </button>
            </div>
            <div className='sp_field sp_field_toggle'>
              <label className='sp_label'>Dans le simulateur</label>
              <button
                className={`sp_toggle ${form.is_in_simulator ? 'sp_toggle_on' : 'sp_toggle_off'}`}
                onClick={() => handleChange('is_in_simulator', !form.is_in_simulator)}
                type='button'
              >
                {form.is_in_simulator ? 'Oui' : 'Non'}
              </button>
            </div>
          </div>
        </div>
      </>}

      <div className='sp_submit_row'>
        {!isNone && !canSave && (
          <span className='sp_validation_hint'>Tous les champs obligatoires doivent être remplis</span>
        )}
        <button
          className='sp_submit_button'
          onClick={handleSubmit}
          disabled={loading || !canSave}
        >
          {loading ? 'Enregistrement...' : spData ? 'Mettre à jour' : 'Enregistrer'}
        </button>
      </div>
    </div>
  )
}

export default UserSignalProvider
